import React from "react";
import { Button, Flex, Link, QueryLoader, Text } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import {
  ComplianceActivityCard,
  AUDIT_LOG_SUMMARY_PREVIEW_LIMIT,
} from "../../ComplianceActivityCard";
import { ComplianceStatusCard } from "../../ComplianceStatusCard";
import { usePositionStatusCounts } from "../../ComplianceStatusCard/usePositionStatusCounts";
import { EntityTable } from "../../ComplianceTable/EntityTable";
import { DashBoardHeader } from "../../DashBoardHeader";
import { DashboardTabs } from "../DashboardTabs";
import { activityCardContainer, cardsRow, statusCardContainer } from "../styles_vanilla.css";
import { useDashboardNavigation } from "../useDashboardNavigation";
import { useDashboardTab } from "../useDashboardTab";
import { centeredText, noPositionWrapper } from "./styles_vanilla.css";

export const ArenaDashboard = ({
  arenaId,
  arenaName,
  launchHome,
}: {
  arenaId: number;
  arenaName?: string;
  launchHome: () => void;
}) => {
  const [activeTab, setActiveTab] = useDashboardTab();
  const { navigateToEntity } = useDashboardNavigation();

  const {
    hasPositions,
    hasComplianceEntities,
    hasNoEntityAccess,
    hasPendingDivestment,
    queryResults,
  } = usePositionStatusCounts({
    arenaId,
  });

  const complianceEnabled = hasComplianceEntities && !hasNoEntityAccess;

  return (
    <Flex flexDirection="column">
      <DashBoardHeader
        config={{
          variant: "arena",
        }}
        tabs={
          hasPositions &&
          complianceEnabled && <DashboardTabs activeTab={activeTab} onChange={setActiveTab} />
        }
      />
      <QueryLoader result={queryResults}>
        {hasPositions && complianceEnabled ? (
          <Flex flexDirection="column" gap={Sizes.LG}>
            {activeTab === "summary" && (
              <>
                <Flex className={cardsRow}>
                  <Flex className={statusCardContainer}>
                    <ComplianceStatusCard
                      arenaId={arenaId}
                      subtitle="All investment entities with compliance enabled"
                    />
                  </Flex>
                  <Flex className={activityCardContainer}>
                    <ComplianceActivityCard
                      arenaId={arenaId}
                      arenaName={arenaName}
                      maxVisibleItems={AUDIT_LOG_SUMMARY_PREVIEW_LIMIT}
                      onViewAll={() => setActiveTab("auditLog")}
                    />
                  </Flex>
                </Flex>
                <EntityTable
                  arenaId={arenaId}
                  onEntityRowClick={navigateToEntity}
                  showPendingDivestment={hasPendingDivestment}
                />
              </>
            )}
            {activeTab === "auditLog" && (
              <ComplianceActivityCard arenaId={arenaId} arenaName={arenaName} />
            )}
          </Flex>
        ) : (
          <Flex flexDirection="column" gap={Sizes.MD} className={noPositionWrapper}>
            {complianceEnabled ? (
              <>
                <Text variant="h3">No positions onboarded</Text>
                <Text variant="regular-md" className={centeredText}>
                  Please check back shortly for updates.
                </Text>
              </>
            ) : (
              <>
                <Text variant="h3">Compliance services are not enabled</Text>
                <Text variant="regular-md" className={centeredText}>
                  Juniper Square offers compliance services and can help run your AML/KYC
                  program.
                  <br />
                  To add compliance services to your account, contact your account manager or{" "}
                  <Link
                    href="https://help.junipersquare.com/hc/en-us/requests/new"
                    isInline
                    shouldOpenInNewTab
                  >
                    support
                  </Link>
                  .
                </Text>
              </>
            )}
            <Button variant="primary" onClick={launchHome}>
              Back to home
            </Button>
          </Flex>
        )}
      </QueryLoader>
    </Flex>
  );
};
